import { GLOBAL, ReferenceByMAEC, ReferenceDatum, ReferenceTarget } from '@resistapp/common/api-types';
import { GetGroup } from '@resistapp/common/assays';
import { ReferenceMetric } from '@resistapp/common/types';
import { Dictionary, chain, get } from 'lodash';

/**
 * Calculates a weighted reference mean for multiple target categories
 *
 * @param referenceData The reference data
 * @param metric The reference metric to use
 * @param selectedTargets The selected targets (categories)
 * @param environmentType The sample type
 * @param projectAssayCounts Dictionary of assay counts for each target in the project
 * @returns The weighted reference mean or null if it can't be calculated
 */
export function calculateWeightedReferenceMean(
  referenceData: ReferenceByMAEC,
  metric: ReferenceMetric,
  selectedTargets: ReferenceTarget[],
  environmentType: string,
  selectedProjectAssays: string[],
  getGroup: GetGroup,
): number | null {
  // Filter to include only L2 targets for which we have reference data
  const targetsWithReferences = selectedTargets.filter(target => {
    const referenceValue = get(
      referenceData,
      [metric, target, environmentType, GLOBAL, 'mean' satisfies keyof ReferenceDatum],
      null,
    ) as number | null;
    return referenceValue !== null;
  });
  if (targetsWithReferences.length === 0) {
    return null;
  }

  // Count assays by target
  const selectedProjectAssayCounts = countAssaysByTarget(selectedProjectAssays, getGroup);

  // Get the total count of assays in the project for the selected targets
  const totalAssayCounts = chain(targetsWithReferences)
    .map(target => selectedProjectAssayCounts[target] || 0)
    .sum()
    .value();
  if (totalAssayCounts === 0) {
    return null;
  }

  // Calculate weighted geometric mean based on the proportion of assays for each target
  // Work in log space, then exponentiate the result (weighted geometric mean)
  const weightedLogSum = chain(targetsWithReferences)
    .sumBy(target => {
      const referenceValue = get(
        referenceData,
        [metric, target, environmentType, GLOBAL, 'mean' satisfies keyof ReferenceDatum],
        null,
      ) as number | null;
      if (referenceValue === null) {
        return 0;
      }
      const weight = get(selectedProjectAssayCounts, target, 0) / totalAssayCounts;
      return Math.log(referenceValue) * weight;
    })
    .value();

  return Math.exp(weightedLogSum);
}

/**
 * Counts the number of assays per target category in the selected targets
 * This is used to weight the reference means when multiple targets are selected
 *
 * @param assayTargetMap Dictionary mapping assays to their target categories
 * @param selectedAssays Array of selected assays
 * @returns Dictionary with target categories as keys and assay counts as values
 */
export function countAssaysByTarget(selectedAssays: string[], getGroup: GetGroup): Dictionary<number> {
  return chain(selectedAssays)
    .map(assay => getGroup(assay, 'l2Target'))
    .filter(Boolean)
    .groupBy(target => target)
    .mapValues(targets => targets.length)
    .value();
}
